import {
  ChoiceOption,
  DateQuestion,
  HorizontalPickerQuestion,
  MultipleChoiceQuestion,
  ProfileQuestion,
  SingleChoiceQuestion,
  VerticalPickerQuestion,
} from './profile-question.model';

/**
 * The app's onboarding questions per track, in the order the app asks them (info screens left out).
 * Each id is the answer's field in `onboarding/quiz-chat` and each option value is what the app
 * stores, both as in the iOS `OnboardingUserConfiguration` / `OnboardingAnswers` (typos included,
 * e.g. "tryingToConcieve"), so the app can read the answers. Labels follow the app's screens.
 * Questions shared between tracks keep the same id so they're stored once.
 */

/** The user's `usageReason`, picked on the app's first screen ("What would you like IVY to help with?"). */
export type OnboardingTrack = 'trackMyCycle' | 'toBecomePregnant' | 'toTrackCurrentPregnancy';

const option = (value: string, label: string, exclusive = false): ChoiceOption => ({ value, label, exclusive });

const yesNo = [option('yes', 'Yes'), option('no', 'No')];

// Shared by every track

const yearOfBirth: VerticalPickerQuestion = {
  id: 'yearOfBirth',
  type: 'vertical-picker',
  title: 'What year were you born?',
  icon: 'birthday',
  units: [{ label: 'year', min: 1940, max: new Date().getFullYear() - 13, subunits: 1, factor: 1 }],
  initial: 2000,
};

/** As in the app: metres and centimetres ("01 | 60 | m"), or feet and inches. Saved in cm or in. */
const height: VerticalPickerQuestion = {
  id: 'height',
  type: 'vertical-picker',
  title: 'How tall are you?',
  units: [
    { label: 'm', min: 1, max: 2.5, subunits: 100, wholeDigits: 2, factor: 1 },
    { label: 'ft', min: 39 / 12, max: 98 / 12, subunits: 12, factor: 0.3048 },
  ],
  initial: 1.6,
};

const weight: VerticalPickerQuestion = {
  id: 'weight',
  type: 'vertical-picker',
  title: 'My current weight is...',
  units: [
    { label: 'kg', min: 30, max: 250, subunits: 10, fractionPrefix: '.', factor: 1 },
    { label: 'lbs', min: 66, max: 551, subunits: 10, fractionPrefix: '.', factor: 0.45359237 },
  ],
  initial: 60,
};

const diet: SingleChoiceQuestion = {
  id: 'dietPreference',
  type: 'single-choice',
  title: 'Which best describes the way you eat?',
  options: [
    option('varied', 'Varied'),
    option('vegetarian', 'Vegetarian'),
    option('vegan', 'Vegan'),
    option('pescatarian', 'Pescatarian'),
    option('other', 'Other'),
  ],
};

const otherApps: MultipleChoiceQuestion = {
  id: 'periodApplications',
  type: 'multiple-choice',
  title: 'What other period tracking apps have you used?',
  skippable: true,
  options: [
    option('Flo', 'Flo'),
    option('Clue', 'Clue'),
    option('Ovia', 'Ovia'),
    option('Other', 'Other'),
    option('None', 'None', true),
  ],
};

// Cycle questions, shared by Track My Cycle and Try to Conceive.
// Regular cycles ask for the usual length; irregular ones for the longest and shortest instead.

const regularPeriods: SingleChoiceQuestion = {
  id: 'regularPeriods',
  type: 'single-choice',
  title: 'Are your cycles usually about the same length?',
  options: yesNo,
};

const cycleDays = (id: string, title: string, initial: number, regular: 'yes' | 'no'): HorizontalPickerQuestion => ({
  id,
  type: 'horizontal-picker',
  title,
  min: 5,
  max: 100,
  initial,
  unit: 'days',
  notSureValue: initial,
  showIf: { questionId: regularPeriods.id, values: [regular] },
});

const cycleQuestions: ProfileQuestion[] = [
  regularPeriods,
  cycleDays('cycleLength', 'How long is your menstrual cycle?', 28, 'yes'),
  cycleDays('longestCycleLength', 'What was your longest cycle in the last 12 months?', 33, 'no'),
  cycleDays('shortestCycleLength', 'What was your shortest cycle in the last 12 months?', 22, 'no'),
  {
    id: 'periodDuration',
    type: 'horizontal-picker',
    title: 'How many days does your period usually last?',
    min: 1,
    max: 15,
    initial: 5,
    unit: 'days',
    notSureValue: 5,
  },
];

const healthGoalOptions = [
  option('period_plan', 'Regulate my cycle naturally'),
  option('getfit_pd', 'Get in better shape'),
  option('weight_loss_pd', 'Get to a healthy weight'),
  option('gethealthy_pd', 'Simply live the healthiest life possible'),
];

const additionalSupportOptions = [
  option('better_sleep_pd', 'Sleep better'),
  option('healthy_aging_pd', 'Support healthy aging'),
  option('none', 'None of the above', true),
];

const topicOptions = [
  option('Mental health', 'Mental wellbeing'),
  option('Pregnancy', 'Pregnancy'),
  option('PCOS', 'PCOS'),
  option('Relationships', 'Relationships'),
  option('none-of-the-above', 'None of the above', true),
];

const trackMyCycle: ProfileQuestion[] = [
  ...cycleQuestions,
  {
    id: 'symptoms',
    type: 'multiple-choice',
    title: 'Do you experience any of these cycle-related symptoms?',
    skippable: true,
    options: [
      option('Painful cramps', 'Painful cramps'),
      option('Strong PMS', 'Strong PMS'),
      option('Heavy menstrual flow', 'Heavy menstrual flow'),
      option('Other', 'Other'),
      option('I don’t experience any cycle related symptoms', "I don't experience cycle related symptoms", true),
    ],
  },
  {
    id: 'contraception',
    type: 'single-choice',
    title: 'Do you use hormonal birth control?',
    skippable: true,
    options: [
      option('contraceptive_pill', 'Contraceptive pills'),
      option('contraceptive_patch', 'Contraceptive patch'),
      option('intrauterine_device', 'Intrauterine device'),
      option('vaginal_ring', 'Hormonal vaginal ring'),
      option('implant', 'Implant'),
      option('other', 'Other'),
      option('none', "I don't use hormonal contraception"),
    ],
  },
  {
    id: 'primaryGoal',
    type: 'single-choice',
    title: "How would you define the health specific goal you're currently trying to achieve?",
    options: healthGoalOptions,
  },
  {
    id: 'secondaryGoals',
    type: 'multiple-choice',
    title: 'What else would you like support with?',
    maxSelections: 2,
    options: additionalSupportOptions,
  },
  {
    id: 'topicsOfInterest',
    type: 'multiple-choice',
    title: 'Which of the following topics might you also be interested in?',
    skippable: true,
    options: topicOptions,
  },
  height,
  weight,
  diet,
  otherApps,
  {
    id: 'pregnancyPrevention',
    type: 'single-choice',
    title: "Do you plan to use IVY's cycle guidance to help prevent pregnancy?",
    options: yesNo,
  },
];

const toBecomePregnant: ProfileQuestion[] = [
  {
    id: 'tryingToConcieve',
    type: 'single-choice',
    title: 'How long have you been trying to conceive?',
    skippable: true,
    options: [
      option('trying-to-concieve-starting', 'Just starting'),
      option('1-3', '1-3 months'),
      option('3-6', '3-6 months'),
      option('6-12', '6-12 months'),
      option('trying-to-concieve-over-a-year', 'Over a year'),
    ],
  },
  {
    id: 'assistedProcedure',
    type: 'multiple-choice',
    title: 'Are you receiving any fertility treatment?',
    skippable: true,
    options: [
      option('Timed intercourse', 'Timed intercourse'),
      option('IUI', 'Intrauterine insemination (IUI)'),
      option('Assisted reproductive technology', 'IVF'),
      option('Other', 'Other'),
      option('None', 'None', true),
    ],
  },
  ...cycleQuestions,
  {
    id: 'fertileWindow',
    type: 'single-choice',
    title: 'Do you track your fertile window to help time sex?',
    skippable: true,
    options: [
      option('Yes', 'Yes'),
      option('heard-but-dont', "I have heard about it, but I don't"),
      option('dont-know-what-a-fertile-window-is', "I don't know what a fertile window is"),
    ],
  },
  {
    id: 'basalBodyTemperature',
    type: 'single-choice',
    title: 'Do you track your basal body temperature?',
    skippable: true,
    options: [
      option('Yes', 'Yes'),
      option('heard-but-dont', "I have heard about it, but I don't"),
      option('dont-know-what-it-is', "I don't know what that is"),
    ],
  },
  {
    id: 'smoking',
    type: 'single-choice',
    title: 'Do you smoke?',
    skippable: true,
    options: yesNo,
  },
  {
    id: 'primaryGoal',
    type: 'single-choice',
    title: 'What other health goals would you like support with?',
    options: healthGoalOptions,
  },
  {
    id: 'secondaryGoals',
    type: 'multiple-choice',
    title: 'Are you also interested in any of the following?',
    maxSelections: 2,
    options: additionalSupportOptions,
  },
  {
    id: 'topicsOfInterest',
    type: 'multiple-choice',
    title: 'Which of the following topics might you also be interested in?',
    skippable: true,
    options: topicOptions,
  },
  height,
  weight,
  {
    id: 'vitaminsAndSupplements',
    type: 'single-choice',
    title: 'Do you take any prenatal vitamins or supplements?',
    skippable: true,
    options: [
      option('vit-complex', 'Yes, a special vitamin complex'),
      option('folic-acid', 'I take folic acid'),
      option('dont-take-supplements', "I currently don't take any vitamins or supplements"),
    ],
  },
  {
    id: 'preparationForConception',
    type: 'single-choice',
    title: 'Have you taken any steps to prepare for pregnancy?',
    skippable: true,
    options: [
      option('lifestyle-changes', 'Lifestyle changes'),
      option('medical-check-up', 'A medical check up'),
      option('alternative-medicine', 'Alternative medicine'),
      option('havent-made-changes', "I haven't made any changes yet"),
    ],
  },
  {
    id: 'menstrualCrampsDescription',
    type: 'single-choice',
    title: 'How would you describe your menstrual cramps?',
    skippable: true,
    options: [
      option('severe-cramps', 'I have severe menstrual cramps'),
      option('moderate-cramps', 'I have moderate menstrual cramps'),
      option('no-crapms', "I don't experience menstrual cramps"),
    ],
  },
  {
    id: 'stressLevel',
    type: 'single-choice',
    title: 'How would you describe your stress lately?',
    skippable: true,
    options: [
      option('stress-low', 'Low'),
      option('stress-moderate', 'Moderate'),
      option('stress-high', 'High'),
      option('stress-overwhelming', 'Overwhelming'),
    ],
  },
  {
    id: 'issues',
    type: 'single-choice',
    title: 'Do you regularly have difficulty with any of these?',
    skippable: true,
    options: [
      option('falling-asleep', 'Falling asleep'),
      option('disturbed-sleep', 'Disturbed sleep'),
      option('waking-up-tired', 'Waking up tired'),
      option('none-of-the-above', 'None of the above'),
    ],
  },
  diet,
  otherApps,
];

// Support My Pregnancy: the date question that follows depends on how the user adds their due date.
// Values match the app's OnboardingPregnancyCalculationType.

const currentPregnancyType: SingleChoiceQuestion = {
  id: 'currentPregnancyType',
  type: 'single-choice',
  title: 'How would you like to add your due date?',
  options: [
    option('lastPeriod', 'First day of my last period'),
    option('dueDate', 'I know my due date'),
    option('ovaryPunction', 'Egg retrieval date'),
    option('embryoTransfer', 'Embryo transfer date'),
  ],
};

/** A pregnancy lasts up to about 42 weeks. */
const PREGNANCY_DAYS = 42 * 7;

const pregnancyDate = (
  id: string,
  method: string,
  title: string,
  minDaysFromToday: number,
  maxDaysFromToday: number,
  extra: Partial<DateQuestion> = {},
): ProfileQuestion => ({
  id,
  type: 'date',
  title,
  minDaysFromToday,
  maxDaysFromToday,
  showIf: { questionId: currentPregnancyType.id, values: [method] },
  ...extra,
});

// The app starts egg retrieval and embryo transfer at dates that both put the pregnancy at 4 weeks 3 days
const toTrackCurrentPregnancy: ProfileQuestion[] = [
  currentPregnancyType,
  pregnancyDate('startPregnancyDate', 'lastPeriod', 'My last period started', -PREGNANCY_DAYS, 0),
  pregnancyDate('dueDate', 'dueDate', 'Due date', 0, PREGNANCY_DAYS),
  pregnancyDate('ovaryPunctionDate', 'ovaryPunction', 'Egg retrieval date', -PREGNANCY_DAYS, 0, { initialDaysFromToday: -17 }),
  pregnancyDate('embryoTransferDate', 'embryoTransfer', 'Embryo transfer date', -PREGNANCY_DAYS, 0, {
    initialDaysFromToday: -12,
    embryoAge: true,
  }),
  {
    id: 'firstPregnancy',
    type: 'single-choice',
    title: 'Is this your first pregnancy?',
    skippable: true,
    options: yesNo,
  },
  {
    id: 'topicsOfInterest',
    type: 'multiple-choice',
    title: 'What else would you like support with?',
    skippable: true,
    options: [
      option('Mental health', 'Mental wellbeing'),
      option('PCOS', 'PCOS'),
      option('Relationships', 'Relationships'),
    ],
  },
  height,
  weight,
  diet,
  otherApps,
];

const TRACK_QUESTIONS: Record<OnboardingTrack, ProfileQuestion[]> = {
  trackMyCycle,
  toBecomePregnant,
  toTrackCurrentPregnancy,
};

/** Every onboarding question for the track, birth year first. */
export function onboardingQuestions(track: OnboardingTrack): ProfileQuestion[] {
  return [yearOfBirth, ...TRACK_QUESTIONS[track]];
}

/** The app's first screen, which picks the track. Only asked in the preview; the chat reads `usageReason`. */
export const trackQuestion: SingleChoiceQuestion = {
  id: 'usageReason',
  type: 'single-choice',
  title: 'What would you like IVY to help with?',
  options: [
    option('trackMyCycle', 'Understand my cycle'),
    option('toBecomePregnant', 'Try to conceive'),
    option('toTrackCurrentPregnancy', 'Support my pregnancy'),
  ],
};

/** The whole onboarding for a track: the track question, then the track's questions. */
export function fullOnboardingQuestions(track: OnboardingTrack): ProfileQuestion[] {
  return [trackQuestion, ...onboardingQuestions(track)];
}
